/**
 * プラットフォーム検出サービス
 * デバイスタイプとNFC機能の利用可能性を判定
 */

export type Platform = 'windows' | 'android' | 'ios' | 'unknown';
export type NFCCapability = 'websocket' | 'web-nfc' | 'none';

interface PlatformInfo {
  platform: Platform;
  isAndroid: boolean;
  isWindows: boolean;
  isIOS: boolean;
  isMobile: boolean;
  hasWebNFC: boolean;
  hasWebSocket: boolean;
  nfcCapability: NFCCapability;
  userAgent: string;
}

class PlatformDetector {
  private platformInfo: PlatformInfo | null = null;

  /**
   * プラットフォーム情報を取得（キャッシュあり）
   */
  getPlatformInfo(): PlatformInfo {
    if (!this.platformInfo) {
      this.platformInfo = this.detectPlatform();
    }
    return this.platformInfo;
  }

  /**
   * プラットフォームを検出
   */
  private detectPlatform(): PlatformInfo {
    const userAgent = navigator.userAgent;
    const isAndroid = /Android/i.test(userAgent);
    const isIOS = /iPhone|iPad|iPod/i.test(userAgent);
    const isWindows = /Windows/i.test(userAgent);
    const isMobile = isAndroid || isIOS || /Mobile/i.test(userAgent);

    // Web NFC API の利用可能性を確認（Android Chrome/Edge のみ）
    const hasWebNFC = 'NDEFReader' in window;

    // WebSocket の利用可能性（基本的にすべてのブラウザで利用可能）
    const hasWebSocket = 'WebSocket' in window;

    // プラットフォームの判定
    let platform: Platform = 'unknown';
    if (isAndroid) {
      platform = 'android';
    } else if (isWindows) {
      platform = 'windows';
    } else if (isIOS) {
      platform = 'ios';
    }

    // NFC機能の判定
    let nfcCapability: NFCCapability = 'none';
    if (isAndroid && hasWebNFC) {
      // AndroidでWeb NFC APIが利用可能
      nfcCapability = 'web-nfc';
    } else if (isWindows && hasWebSocket) {
      // WindowsでWebSocket経由のNFC（PaSoRi）が利用可能
      nfcCapability = 'websocket';
    }

    return {
      platform,
      isAndroid,
      isWindows,
      isIOS,
      isMobile,
      hasWebNFC,
      hasWebSocket,
      nfcCapability,
      userAgent,
    };
  }

  /**
   * NFC機能が利用可能かチェック
   */
  isNFCAvailable(): boolean {
    const info = this.getPlatformInfo();
    return info.nfcCapability !== 'none';
  }

  /**
   * Web NFC APIが利用可能かチェック（Android専用）
   */
  isWebNFCAvailable(): boolean {
    const info = this.getPlatformInfo();
    return info.nfcCapability === 'web-nfc';
  }

  /**
   * WebSocket経由のNFCが利用可能かチェック（Windows専用）
   */
  isWebSocketNFCAvailable(): boolean {
    const info = this.getPlatformInfo();
    return info.nfcCapability === 'websocket';
  }

  /**
   * HTTPS環境かチェック（Web NFC APIに必要）
   */
  isSecureContext(): boolean {
    return window.isSecureContext === true;
  }

  /**
   * PWAとして実行されているかチェック
   */
  isPWA(): boolean {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true ||
      document.referrer.includes('android-app://')
    );
  }

  /**
   * NFC権限をリクエスト（Android用）
   */
  async requestNFCPermission(): Promise<boolean> {
    if (!this.isWebNFCAvailable()) {

      return false;
    }

    try {
      // Web NFC APIの権限をリクエスト
      const ndef = new (window as any).NDEFReader();
      await ndef.scan();

      return true;
    } catch (error) {
      console.error('NFC permission denied:', error);
      return false;
    }
  }

  /**
   * プラットフォーム固有の制限事項を取得
   */
  getPlatformLimitations(): string[] {
    const info = this.getPlatformInfo();
    const limitations: string[] = [];

    if (info.isAndroid) {
      if (!info.hasWebNFC) {
        limitations.push('Web NFC APIがサポートされていません（Chrome/Edgeを使用してください）');
      }
      if (!this.isSecureContext()) {
        limitations.push('HTTPS接続が必要です');
      }
    } else if (info.isWindows) {
      limitations.push('Sony PaSoRi RC-S380とNFCサービスが必要です');
    } else if (info.isIOS) {
      limitations.push('iOSではNFC機能は現在サポートされていません');
    } else {
      limitations.push('このプラットフォームではNFC機能はサポートされていません');
    }

    return limitations;
  }
}

// シングルトンインスタンス
export const platformDetector = new PlatformDetector();